import { Component, ChangeDetectionStrategy, input, signal, computed, OnInit, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import {
  Finding,
  FindingStatus,
  ControlDomain,
  PolicyDocument,
  GapAcknowledgment,
  PolicyGapAnalysisService,
  FINDING_STATUS_CONFIG,
  DOMAIN_DISPLAY_NAMES,
} from '../../services/policy-gap-analysis.service';
import { FindingStatusBadgeComponent } from '../finding-status-badge/finding-status-badge.component';
import { AcknowledgeDialogComponent, AcknowledgmentFormData } from '../acknowledge-dialog/acknowledge-dialog.component';

@Component({
  selector: 'app-analysis-drilldown',
  standalone: true,
  imports: [CommonModule, FormsModule, FindingStatusBadgeComponent, AcknowledgeDialogComponent],
  templateUrl: './analysis-drilldown.component.html',
  styleUrl: './analysis-drilldown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysisDrilldownComponent implements OnInit {
  private gapService = inject(PolicyGapAnalysisService);

  findings = input.required<Finding[]>();
  documents = input.required<PolicyDocument[]>();
  filterDomain = input<ControlDomain | null>(null);
  filterDocumentId = input<number | null>(null);

  // Filter state
  public statusFilter = signal<FindingStatus | 'all'>('all');
  public domainFilter = signal<ControlDomain | 'all'>('all');
  public documentFilter = signal<number | 'all'>('all');
  public searchQuery = signal<string>('');
  public acknowledgeFilter = signal<'all' | 'acknowledged' | 'unacknowledged'>('all');
  public sortField = signal<string>('domain');
  public sortDirection = signal<'asc' | 'desc'>('asc');

  // Expanded rows
  public expandedRows = new Set<number>();

  // Acknowledge dialog state
  public showAcknowledgeDialog = signal<boolean>(false);
  public selectedFindingForAck = signal<Finding | null>(null);

  // Acknowledgments from service
  private acknowledgmentsMap = this.gapService.getAcknowledgments();

  // Available filter options
  public readonly statusOptions = Object.entries(FINDING_STATUS_CONFIG).map(([key, val]) => ({
    value: key as FindingStatus,
    label: val.label,
  }));

  public readonly domainOptions = Object.entries(DOMAIN_DISPLAY_NAMES).map(([key, val]) => ({
    value: key as ControlDomain,
    label: val,
  }));

  constructor() {
    // Apply initial filters from inputs
    effect(() => {
      const domain = this.filterDomain();
      if (domain) {
        this.domainFilter.set(domain);
      }
    });

    effect(() => {
      const docId = this.filterDocumentId();
      if (docId) {
        this.documentFilter.set(docId);
      }
    });
  }

  ngOnInit(): void {
    // Apply initial filters
    if (this.filterDomain()) {
      this.domainFilter.set(this.filterDomain()!);
    }
    if (this.filterDocumentId()) {
      this.documentFilter.set(this.filterDocumentId()!);
    }
  }

  public filteredFindings = computed(() => {
    let results = this.findings();
    const ackMap = this.acknowledgmentsMap();

    const status = this.statusFilter();
    if (status !== 'all') {
      results = results.filter((f) => f.status === status);
    }

    const domain = this.domainFilter();
    if (domain !== 'all') {
      results = results.filter((f) => f.domain === domain);
    }

    const docId = this.documentFilter();
    if (docId !== 'all') {
      results = results.filter((f) => f.document_id === docId);
    }

    const ackFilter = this.acknowledgeFilter();
    if (ackFilter === 'acknowledged') {
      results = results.filter((f) => ackMap.has(f.id));
    } else if (ackFilter === 'unacknowledged') {
      results = results.filter((f) => !ackMap.has(f.id));
    }

    const query = this.searchQuery().toLowerCase().trim();
    if (query) {
      results = results.filter(
        (f) =>
          f.title.toLowerCase().includes(query) ||
          f.explanation.toLowerCase().includes(query) ||
          f.normalized_text.toLowerCase().includes(query)
      );
    }

    // Sort
    const field = this.sortField();
    const dir = this.sortDirection();
    results = [...results].sort((a, b) => {
      const aVal = (a as any)[field] ?? '';
      const bVal = (b as any)[field] ?? '';
      const comparison = String(aVal).localeCompare(String(bVal));
      return dir === 'asc' ? comparison : -comparison;
    });

    return results;
  });

  public resultCount = computed(() => this.filteredFindings().length);

  public toggleRow(findingId: number): void {
    if (this.expandedRows.has(findingId)) {
      this.expandedRows.delete(findingId);
    } else {
      this.expandedRows.add(findingId);
    }
  }

  public isRowExpanded(findingId: number): boolean {
    return this.expandedRows.has(findingId);
  }

  public toggleSort(field: string): void {
    if (this.sortField() === field) {
      this.sortDirection.update((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      this.sortField.set(field);
      this.sortDirection.set('asc');
    }
  }

  public getSortIcon(field: string): string {
    if (this.sortField() !== field) return '⇅';
    return this.sortDirection() === 'asc' ? '↑' : '↓';
  }

  public getDocumentName(docId: number): string {
    return this.documents().find((d) => d.id === docId)?.file_name ?? `Document #${docId}`;
  }

  public getDomainDisplayName(domain: ControlDomain): string {
    return DOMAIN_DISPLAY_NAMES[domain];
  }

  public clearFilters(): void {
    this.statusFilter.set('all');
    this.domainFilter.set('all');
    this.documentFilter.set('all');
    this.searchQuery.set('');
    this.acknowledgeFilter.set('all');
  }

  public getSeverityClass(severity: string): string {
    const map: Record<string, string> = {
      critical: 'text-danger fw-bold',
      high: 'text-danger',
      medium: 'text-warning',
      low: 'text-muted',
      informational: 'text-info',
    };
    return map[severity] || '';
  }

  // Acknowledgment methods

  public isAcknowledged(findingId: number): boolean {
    return this.acknowledgmentsMap().has(findingId);
  }

  public getAcknowledgment(findingId: number): GapAcknowledgment | undefined {
    return this.acknowledgmentsMap().get(findingId);
  }

  public onAcknowledge(finding: Finding): void {
    this.selectedFindingForAck.set(finding);
    this.showAcknowledgeDialog.set(true);
  }

  public onAcknowledgeSaved(data: AcknowledgmentFormData): void {
    const finding = this.selectedFindingForAck();
    if (!finding) return;

    this.gapService.acknowledgeFinding(finding.id, data).subscribe(() => {
      this.showAcknowledgeDialog.set(false);
      this.selectedFindingForAck.set(null);
    });
  }

  public onAcknowledgeCancelled(): void {
    this.showAcknowledgeDialog.set(false);
    this.selectedFindingForAck.set(null);
  }
}
